/**
 * VEO3 视频生成节点 - 支持多种生成模式
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './Veo3VideoNode.css';

const apiService = new ApiService();

// 工具函数：判断数组是否相等
const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

// 工具函数：合并图片列表（保持原有顺序）
const mergeImageLists = (prev = [], next = []) => {
  const nextQueue = [...next];
  const reordered = [];

  prev.forEach((url) => {
    const index = nextQueue.indexOf(url);
    if (index !== -1) {
      reordered.push(url);
      nextQueue.splice(index, 1);
    }
  });

  return [...reordered, ...nextQueue];
};

// VEO3 模型配置
const modelOptions = [
  { 
    value: 'veo3.1', 
    label: 'VEO3.1 快速 ⭐', 
    speed: 'fast', 
    quality: 'standard',
    imageSupport: 'first-last',
    description: '新一代模型，效果更好'
  },
  { 
    value: 'veo3', 
    label: 'VEO3 快速', 
    speed: 'fast', 
    quality: 'standard',
    imageSupport: 'first',
    description: '快速生成，标准质量'
  },
  { 
    value: 'veo3.1-pro', 
    label: 'VEO3.1 高质量', 
    speed: 'slow', 
    quality: 'high',
    imageSupport: 'first-last',
    description: '高质量输出，处理时间较长'
  },
  { 
    value: 'veo3-pro', 
    label: 'VEO3 高质量', 
    speed: 'slow', 
    quality: 'high',
    imageSupport: 'first',
    description: '高质量输出，处理时间较长'
  },
  { 
    value: 'veo3.1-components', 
    label: 'VEO3.1 多图参考', 
    speed: 'medium', 
    quality: 'standard',
    imageSupport: 'reference',
    description: '支持1-3张参考图片风格融合'
  },
];

// 视频比例选项
const sizeOptions = [
  { value: '16:9', label: '横屏 (16:9)', icon: '🖥️', description: '电脑、电视、横屏短视频' },
  { value: '9:16', label: '竖屏 (9:16)', icon: '📱', description: '手机、抖音、快手' },
];

export const Veo3VideoNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes); // 添加节点监听
  const { readOnly } = useProject();
  
  const [model, setModel] = useState(data.model || 'veo3.1');
  const [size, setSize] = useState(data.size || '16:9');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImages, setConnectedImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      model,
      size,
    });
  }, [id, model, size, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      
      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'prompt') return true;
      
      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        return sourceNode?.type === 'promptInput';
      }
      
      return false;
    });
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  // 获取连接到此节点的图片节点
  const getConnectedImageNodes = useCallback(() => {
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      
      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'image') return true;
      
      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'videoTask'];
        return validImageTypes.includes(sourceNode?.type);
      }
      
      return false;
    });
    
    console.log(`Veo3VideoNode ${id} - 连接到图片输入的边:`, connectedEdges.length);
    
    const imageUrls = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`Veo3VideoNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Veo3VideoNode ${id} - 从图片参考节点获取图片:`, url);
        }
      }
      
      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        // 只使用已上传到云端的图片，过滤掉本地 base64 预览
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Veo3VideoNode ${id} - 从手绘节点获取图片:`, url);
        } else if (sourceNode.data?.isLocalPreview) {
          console.warn(`Veo3VideoNode ${id} - 手绘节点图片为本地预览，跳过`);
        }
      }
      
      // 从图片任务节点获取生成的图片
      if (sourceNode && sourceNode.type === 'imageTask') {
        console.log(`Veo3VideoNode ${id} - 任务节点数据:`, {
          status: sourceNode.data?.status,
          generatedImages: sourceNode.data?.generatedImages,
          imagesCount: sourceNode.data?.generatedImages?.length
        });
        
        if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
          // 获取所有生成的图片
          sourceNode.data.generatedImages.forEach(img => {
            const url = typeof img === 'string' ? img : img.url;
            if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
              imageUrls.push(url);
              console.log(`Veo3VideoNode ${id} - 从任务节点获取图片:`, url);
            }
          });
        }
      }
      
      // 从视频任务节点获取生成的视频封面
      if (sourceNode && sourceNode.type === 'videoTask') {
        if (sourceNode.data?.coverImageUrl) {
          const url = sourceNode.data.coverImageUrl;
          if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`Veo3VideoNode ${id} - 从视频任务节点获取封面:`, url);
          }
        }
      }
    });
    
    console.log(`Veo3VideoNode ${id} - 总共获取到 ${imageUrls.length} 张图片`);
    return imageUrls;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片列表
  useEffect(() => {
    const imageUrls = getConnectedImageNodes();
    setConnectedImages((prev) => {
      const merged = mergeImageLists(prev, imageUrls);
      return arraysEqual(prev, merged) ? prev : merged;
    });
  }, [getConnectedImageNodes]);

  // 处理拖动开始
  const handleDragStart = (e, index) => {
    if (readOnly) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  // 处理拖动结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 处理拖动经过
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // 处理放置
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || readOnly) return;

    const newImages = [...connectedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setConnectedImages(newImages);
    setDraggedIndex(null);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n'); // 合并多个提示词
      console.log('使用提示词:', prompt);
      
      // 2. 获取连接的参考图片 - 使用已连接的图片列表
      console.log('获取到的图片数量:', connectedImages.length);
      
      // 3. 根据模型类型验证图片数量
      const selectedModel = modelOptions.find(m => m.value === model);
      if (selectedModel.imageSupport === 'reference' && (connectedImages.length < 1 || connectedImages.length > 3)) {
        setError('VEO3.1 多图参考模式需要连接 1-3 张参考图片');
        toast.error('请连接 1-3 张参考图片');
        setIsGenerating(false);
        return;
      }
      
      // 4. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 5. 构建输入参数
      const inputParams = {
        prompt: prompt,
        model: model,
        size: size,
      };
      
      // 根据模型类型添加图片参数
      if (selectedModel.imageSupport === 'reference') {
        // 多图参考模式：支持1-3张参考图
        if (connectedImages.length >= 1) {
          inputParams.ref_img1 = connectedImages[0];
        }
        if (connectedImages.length >= 2) {
          inputParams.ref_img2 = connectedImages[1];
        }
        if (connectedImages.length >= 3) {
          inputParams.ref_img3 = connectedImages[2];
        }
      } else if (selectedModel.imageSupport === 'first-last') {
        // 首尾帧模式：支持首帧和尾帧
        if (connectedImages.length >= 1) {
          inputParams.first_frame = connectedImages[0];
        }
        if (connectedImages.length >= 2) {
          inputParams.last_frame = connectedImages[1];
        }
      } else if (selectedModel.imageSupport === 'first') {
        // 首帧模式：只支持首帧
        if (connectedImages.length >= 1) {
          inputParams.first_frame = connectedImages[0];
        }
      }
      
      console.log('VEO3 输入参数:', inputParams);
      
      // 6. 调用 VEO3 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: 'st-ai/veo3_api',
          task_type: 'coze_api',
          input_params: inputParams
        }
      );
      
      console.log('VEO3 任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        
        // 根据是否使用图片显示不同的提示
        let toastMessage = '文生视频任务已创建！';
        if (selectedModel.imageSupport === 'reference') {
          toastMessage = '多图参考视频任务已创建！';
        } else if (connectedImages.length > 0) {
          toastMessage = connectedImages.length >= 2 ? '首尾帧视频任务已创建！' : '首帧视频任务已创建！';
        }
        toast.success(toastMessage);
        
        // 7. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        // 8. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: connectedImages.length > 0 ? 'image-to-video' : 'text-to-video',
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 9. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建VEO3视频任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('VEO3 视频生成失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 获取当前选中的模型信息
  const selectedModel = modelOptions.find(m => m.value === model);
  const selectedSize = sizeOptions.find(s => s.value === size);

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'videoTask'];
      return validImageTypes.includes(sourceNode?.type);
    }
    
    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'veo3Video', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="Veo3VideoNode-container">
      {/* 复制按钮 */}
      <button 
        className="Veo3VideoNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="Veo3VideoNode-handle Veo3VideoNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '25%' }}
      >
        <div 
          className="Veo3VideoNode-handle-label Veo3VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="Veo3VideoNode-handle Veo3VideoNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '75%' }}
      >
        <div 
          className="Veo3VideoNode-handle-label Veo3VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多1张）"
        >
          图片
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="Veo3VideoNode-handle Veo3VideoNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="Veo3VideoNode-title">VEO3 视频生成</div>

      {/* 内容区域 */}
      <div className="Veo3VideoNode-wrapper">
        {/* 模型选择 */}
        <div className="Veo3VideoNode-field">
          <label className="Veo3VideoNode-label">视频模型</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={isGenerating || readOnly}
            className="Veo3VideoNode-select"
          >
            {modelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="Veo3VideoNode-hint-small">
            {selectedModel?.description}
          </div>
        </div>

        {/* 视频比例选择 */}
        <div className="Veo3VideoNode-field">
          <label className="Veo3VideoNode-label">视频比例</label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            disabled={isGenerating || readOnly}
            className="Veo3VideoNode-select"
          >
            {sizeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label}
              </option>
            ))}
          </select>
          <div className="Veo3VideoNode-hint-small">
            {selectedSize?.description}
          </div>
        </div>

        {/* 模型特性信息 */}
        <div className="Veo3VideoNode-info">
          <span className="Veo3VideoNode-info-badge">
            {selectedModel?.speed === 'fast' ? '⚡ 快速' : selectedModel?.speed === 'slow' ? '🎨 高质量' : '⚖️ 中速'}
          </span>
          <span className="Veo3VideoNode-info-badge">
            {selectedModel?.quality === 'high' ? '✨ 高清' : '💫 标清'}
          </span>
          <span className="Veo3VideoNode-info-badge">
            {size === '16:9' ? '🖥️ 横屏' : '📱 竖屏'}
          </span>
        </div>

        {/* 参考图片展示 */}
        {connectedImages.length > 0 && (
          <div className="Veo3VideoNode-field">
            <label className="Veo3VideoNode-label">
              参考图片 ({connectedImages.length}张
              {selectedModel?.imageSupport === 'reference' && (connectedImages.length < 1 || connectedImages.length > 3) && ' - ⚠️ 需要1-3张'}
              {selectedModel?.imageSupport === 'first-last' && connectedImages.length > 2 && ' - ⚠️ 将使用前2张'}
              {selectedModel?.imageSupport === 'first' && connectedImages.length > 1 && ' - ⚠️ 将使用第1张'}
              )
            </label>
            <div className="Veo3VideoNode-image-grid nodrag">
              {connectedImages.map((imageUrl, index) => {
                // 判断图片是否会被使用
                let isActive = false;
                let label = '';
                
                if (selectedModel?.imageSupport === 'reference') {
                  // 多图参考：前3张
                  isActive = index < 3;
                  if (index === 0) label = '参考1';
                  else if (index === 1) label = '参考2';
                  else if (index === 2) label = '参考3';
                } else if (selectedModel?.imageSupport === 'first-last') {
                  // 首尾帧：前2张
                  isActive = index < 2;
                  if (index === 0) label = '首帧';
                  else if (index === 1) label = '尾帧';
                } else if (selectedModel?.imageSupport === 'first') {
                  // 首帧：第1张
                  isActive = index === 0;
                  if (index === 0) label = '首帧';
                }
                
                return (
                  <div
                    key={`${imageUrl}-${index}`}
                    className={`Veo3VideoNode-image-thumb nodrag ${
                      !isActive ? 'Veo3VideoNode-image-inactive' : ''
                    } ${draggedIndex === index ? 'Veo3VideoNode-image-dragging' : ''}`}
                    draggable={!readOnly}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <img 
                      src={imageUrl} 
                      alt={`参考图 ${index + 1}`}
                      className="Veo3VideoNode-thumb-img"
                    />
                    <div className="Veo3VideoNode-thumb-index">
                      {index + 1}
                    </div>
                    {isActive && label && (
                      <div className="Veo3VideoNode-thumb-label">
                        {label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 图片支持提示 */}
        <div className="Veo3VideoNode-hint">
          {selectedModel?.imageSupport === 'reference' ? (
            <>
              🎨 <strong>多图参考模式</strong><br />
              需要连接 <strong>1-3 张</strong>参考图片进行风格融合
            </>
          ) : selectedModel?.imageSupport === 'first-last' ? (
            <>
              🎬 <strong>首尾帧模式</strong><br />
              支持首帧图片（必选）+ 尾帧图片（可选）
            </>
          ) : selectedModel?.imageSupport === 'first' ? (
            <>
              🖼️ <strong>首帧模式</strong><br />
              支持 1 张首帧图片（可选）
            </>
          ) : null}
          <br />
          💡 不连接图片时为<strong>文生视频</strong>模式
        </div>

        {/* 使用说明 */}
        <div className="Veo3VideoNode-usage">
          <div className="Veo3VideoNode-usage-title">📝 使用说明：</div>
          <ol className="Veo3VideoNode-usage-list">
            <li>连接文本输入节点（必需，<strong>英文提示词</strong>）</li>
            <li>根据模型类型连接图片节点（可选）</li>
            <li>点击"创建视频任务"按钮</li>
          </ol>
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`Veo3VideoNode-generate ${isGenerating || readOnly ? 'Veo3VideoNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建视频任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="Veo3VideoNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

Veo3VideoNode.displayName = 'Veo3VideoNode';

